/**
 * itemActions.js — board-item actions that write AND hand back an undo: the row actions
 * (status, rename, qty, urgent, move, delete) and the list editor's "these names now live
 * in another list" / "stop suggesting these names here". A refused write calls `onError`
 * (data/errors.js) — Firestore has rolled it back on screen, so the user must hear why.
 */
import { setItemStatus, setItemDone, updateItem, removeItem, restoreItem } from './items';
import { moveNames, hideNames, restoreHiddenNames } from './lists';
import { saveFailedNotifier } from './errors';
import { normalizeName } from '../logic/names';
import { planRoutes, planHide } from '../logic/lists';

const ignore = () => {};

/**
 * Plan + write "these names now live in `toId`": the route entries (so suggestions and
 * new adds follow) and the open items that move with them, in one batch. Returns the
 * undo (or null when nothing changes).
 */
export function moveNamesTo({ names, fromId, toId, boardItems = [], lists, onError = ignore }) {
  const routes = planRoutes({
    names, fromId, toId, route: lists.route, routes: lists.routes,
    sources: [...new Set([fromId, ...lists.active.map(l => l.id)])],
  });
  const keys = new Set(names.map(normalizeName));
  const items = boardItems
    .filter(i => i.category === fromId && keys.has(normalizeName(i.name)))
    .map(i => ({ id: i.id, category: toId, from: i.category }));
  if (!routes.length && !items.length) return null;
  moveNames({ routes, items: items.map(({ id, category }) => ({ id, category })) }).catch(onError);
  return () => moveNames({
    routes: routes.map(r => ({ key: r.key, next: r.prev })),
    items: items.map(i => ({ id: i.id, category: i.from })),
  }).catch(onError);
}

/**
 * Stop suggesting `names` in list `listId` (the list editor's Remove): one write to the
 * routes doc. Notes made until now stop counting there; History and the open list are
 * untouched, and noting a name again brings it back. Returns the undo (null = nothing to do).
 */
export function hideNamesIn({ names, listId, lists, onError = ignore }) {
  const plan = planHide({ names, listId, hidden: lists.hidden });
  if (!plan.length) return null;
  hideNames(plan.map(p => p.key)).catch(onError);
  return () => restoreHiddenNames(plan).catch(onError);
}

/**
 * What a board row can do — ONE object for the whole board, each method taking the
 * item. (Building a set of handlers per row on every render made every memoized row
 * re-render on any change.) Delete and move toast with an undo; a move is remembered,
 * so the item is suggested in its new list from then on.
 *
 *   setStatus(item, status) · setDone(item, done) · rename(item, name) · setQty(item, qty)
 *   setUrgent(item, urgent) · move(item, listId) · remove(item)
 *
 * `lists` = the ListsCtx state; `listName(id)` names a list in the toast.
 */
export function makeItemActions({ author, notify, t, lists, listName }) {
  const failed = saveFailedNotifier(notify, t);
  return {
    setStatus: (it, status) => setItemStatus(it.id, status, author).catch(failed),
    setDone: (it, done) => setItemDone(it.id, done, author).catch(failed),
    rename: (it, name) => updateItem(it.id, { name }).catch(failed),
    setQty: (it, qty) => updateItem(it.id, { qty }).catch(failed),
    setUrgent: (it, urgent) => updateItem(it.id, { urgent }).catch(failed),
    move: (it, listId) => {
      if (listId === it.category) return;
      const undo = moveNamesTo({ names: [it.name], fromId: it.category, toId: listId, boardItems: [it], lists, onError: failed });
      notify(t('item.movedTo', { list: listName(listId) }), undo || undefined);
    },
    remove: it => {
      removeItem(it.id).catch(failed);
      // Undo puts the same item back as it was (status, qty, urgent, who added it).
      notify(t('item.removed', { name: it.name }), () => restoreItem(it).catch(failed));
    },
  };
}
